/**
 * Unified image upload engine for every entry point (folder photos, central gallery, add-to-deal).
 *
 * Behaviour for all callers:
 *  - one POST per file, at most {@link IMAGE_UPLOAD_CONCURRENCY} in flight;
 *  - every request has an {@link IMAGE_UPLOAD_TIMEOUT_MS} AbortController timeout (no hung
 *    requests on flaky mobile networks);
 *  - on failure / timeout / "server saved nothing" the file is enqueued to the IndexedDB
 *    outbox (unless `queueOnFailure: false`) so it is never silently lost;
 *  - GPS / capture-time hints follow the per-source rules in `gallery-upload-gps.ts`
 *    (camera = device GPS + now; library/drag-drop = EXIF only).
 */
import { appendGpsHintsToFormData, type GpsUploadSource } from "@/lib/gallery-upload-gps";
import {
  enqueueFolderPhotosUpload,
  type ImageUploadOutboxContext,
} from "@/lib/image-upload-outbox";

export const IMAGE_UPLOAD_CONCURRENCY = 3;
export const IMAGE_UPLOAD_TIMEOUT_MS = 60_000;

export type ImageUploadResult = {
  /** Files the server confirmed it saved. */
  uploaded: number;
  /** Files stored in the outbox for later retry. */
  queued: number;
  /** Files dropped without queueing (only when `queueOnFailure: false`). */
  failed: number;
  /** Concatenated `photos` arrays from every successful response. */
  photos: unknown[];
};

export type UploadImagesParams = {
  uploadUrl: string;
  files: File[];
  /** Per-file pick source (same length as `files`). Omit => all treated as `library`. */
  sources?: GpsUploadSource[];
  /** Extra multipart string fields sent with every per-file request (e.g. subfolderType, dealId). */
  extraFields?: Record<string, string | undefined>;
  /** Stored with queued items so the outbox can replay + run follow-ups. */
  context: ImageUploadOutboxContext;
  /** Multipart field name for the file. Default `"files"`. */
  fileFieldName?: string;
  concurrency?: number;
  timeoutMs?: number;
  /** Enqueue failed files to the outbox. Default `true`. */
  queueOnFailure?: boolean;
  /** Called once per confirmed file with the parsed JSON response. */
  onEachSuccess?: (response: unknown) => void;
};

function cleanFields(extra?: Record<string, string | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      if (typeof v === "string" && v.trim() !== "") out[k] = v;
    }
  }
  return out;
}

async function postWithTimeout(url: string, fd: FormData, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, {
      method: "POST",
      credentials: "include",
      body: fd,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

export async function uploadImages(opts: UploadImagesParams): Promise<ImageUploadResult> {
  const {
    uploadUrl,
    files,
    sources,
    extraFields,
    context,
    fileFieldName = "files",
    concurrency = IMAGE_UPLOAD_CONCURRENCY,
    timeoutMs = IMAGE_UPLOAD_TIMEOUT_MS,
    queueOnFailure = true,
    onEachSuccess,
  } = opts;

  const result: ImageUploadResult = { uploaded: 0, queued: 0, failed: 0, photos: [] };
  const n = files.length;
  if (n === 0) return result;

  const fields = cleanFields(extraFields);

  const enqueue = async (file: File, gpsHints: string, capturedAtHints?: string) => {
    if (!queueOnFailure) {
      result.failed += 1;
      return;
    }
    try {
      await enqueueFolderPhotosUpload({
        url: uploadUrl,
        files: [file],
        gpsHints,
        capturedAtHints,
        fields,
        fileFieldName,
        context,
      });
      result.queued += 1;
    } catch {
      // IndexedDB unavailable (private mode / quota): count as failed instead of throwing.
      result.failed += 1;
    }
  };

  const runOne = async (i: number) => {
    const file = files[i]!;
    const fd = new FormData();
    fd.append(fileFieldName, file);
    for (const [k, v] of Object.entries(fields)) fd.append(k, v);
    const source = sources?.[i];
    await appendGpsHintsToFormData(fd, [file], source ? [source] : undefined);
    const gpsHints = String(fd.get("gpsHints") ?? "[]");
    const capRaw = fd.get("capturedAtHints");
    const capturedAtHints = capRaw != null && capRaw !== "" ? String(capRaw) : undefined;

    let r: Response;
    try {
      r = await postWithTimeout(uploadUrl, fd, timeoutMs);
    } catch {
      await enqueue(file, gpsHints, capturedAtHints);
      return;
    }
    if (!r.ok) {
      await enqueue(file, gpsHints, capturedAtHints);
      return;
    }

    let response: unknown = null;
    try {
      response = await r.json();
    } catch {
      response = null;
    }
    const photos = (response as { photos?: unknown[] } | null)?.photos;
    // Server returned 2xx but persisted nothing -> treat as failure and requeue.
    if (Array.isArray(photos) && photos.length === 0) {
      await enqueue(file, gpsHints, capturedAtHints);
      return;
    }

    result.uploaded += 1;
    if (Array.isArray(photos)) result.photos.push(...photos);
    onEachSuccess?.(response);
  };

  const limit = Math.max(1, Math.min(concurrency, n));
  let next = 0;
  const worker = async () => {
    for (;;) {
      const i = next++;
      if (i >= n) break;
      await runOne(i);
    }
  };
  await Promise.all(Array.from({ length: limit }, () => worker()));

  return result;
}
